import type { LocalBridgeConnection } from "./localBridgeApi";

export type BridgeReadiness = {
  tone: "ready" | "pending" | "warning" | "offline";
  label: string;
  detail: string;
};

export function hasRecentBridgeHeartbeat(value?: string, now = Date.now(), maxAgeMs = 150_000) {
  if (!value) return false;
  const heartbeatAt = Date.parse(value);
  if (!Number.isFinite(heartbeatAt)) return false;
  const age = now - heartbeatAt;
  return age >= -60_000 && age <= maxAgeMs;
}

export function bridgeReadiness(connection: LocalBridgeConnection, now = Date.now()): BridgeReadiness {
  if (connection.status === "revoked" || connection.state === "revoked") {
    return { tone: "offline", label: "Revocado", detail: "La conexión fue bloqueada en AWS." };
  }
  if (connection.status === "paused" || connection.state === "paused") {
    return { tone: "pending", label: "Pausado", detail: "La conexión está guardada, pero no recibe mensajes." };
  }
  if (["provisioning", "awaiting_qr", "authenticated"].includes(connection.state)) {
    return {
      tone: "pending",
      label: connection.state === "provisioning" ? "Falta instalar" : connection.state === "awaiting_qr" ? "Falta escanear QR" : "Vinculando",
      detail: "La conexión todavía no está lista para operar.",
    };
  }
  const heartbeatAt = Date.parse(connection.lastHeartbeatAt ?? "");
  const heartbeatAge = Number.isFinite(heartbeatAt) ? now - heartbeatAt : Number.POSITIVE_INFINITY;
  if (connection.state === "ready" && hasRecentBridgeHeartbeat(connection.lastHeartbeatAt, now)) {
    return { tone: "ready", label: "Funcionando", detail: "WhatsApp y AWS están comunicándose correctamente." };
  }
  if (connection.state === "ready" && heartbeatAge <= 300_000) {
    return { tone: "warning", label: "Con demora", detail: "El último pulso llegó hace más de dos minutos." };
  }
  if (["degraded", "error"].includes(connection.state)) {
    return { tone: "warning", label: "Revisar", detail: connection.lastError || "La conexión informó un problema." };
  }
  return { tone: "offline", label: "Sin señal", detail: "No hay comunicación reciente con el equipo que mantiene WhatsApp." };
}

export function relativeHeartbeat(value?: string, now = Date.now()) {
  if (!value) return "Sin pulsos recibidos";
  const time = Date.parse(value);
  if (!Number.isFinite(time)) return "Fecha desconocida";
  const seconds = Math.max(0, Math.round((now - time) / 1_000));
  if (seconds < 10) return "Ahora";
  if (seconds < 60) return `Hace ${seconds} s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `Hace ${minutes} min`;
  const hours = Math.round(minutes / 60);
  return `Hace ${hours} h`;
}
